import {
  createFileRoute,
  redirect,
  retainSearchParams,
} from "@tanstack/react-router";
import { fallback, zodValidator } from "@tanstack/zod-adapter";
import { z } from "zod";
import Verification from "@/components/_Verification";
import { EMAIL_KEY, FIRM_CODE_KEY, LANGUAGE_KEY } from "@/lib/constants";
import { safeJsonParse } from "@/lib/utils";

// Search params are optional, so we need to set default values
// See: https://tanstack.com/router/latest/docs/framework/react/guide/search-params
const searchSchema = z.object({
  // Firm code is a number, but we need to set a default value of 0 to allow for the case where the firm code is not in the url
  firmcode: fallback(z.coerce.string().min(8), "").default(""),
  email: fallback(z.string().email(), "").default(""),
  lng: fallback(z.enum(["en", "es"]), "en").default("en"),
  redirect: fallback(z.string(), "").default(""),
});

export type SearchSchema = Partial<z.infer<typeof searchSchema>>;

export const Route = createFileRoute("/")({
  component: Verification,
  validateSearch: zodValidator(searchSchema),
  search: {
    middlewares: [retainSearchParams(true)],
  },
  beforeLoad: async ({ location }) => {
    const searchParams = new URLSearchParams(location.search);

    const firmCodeParam = searchParams.get("firmcode");
    const emailParam = searchParams.get("email");

    // Note: in the past we've stored values as JSON strings, so we need to parse them.
    // In the future we will store them as strings.
    const storedFirmCode = safeJsonParse<string>(
      localStorage.getItem(FIRM_CODE_KEY) || "",
    );
    const storedEmail = safeJsonParse<string>(
      localStorage.getItem(EMAIL_KEY) || "",
    );
    const storedLanguage = safeJsonParse<string>(
      localStorage.getItem(LANGUAGE_KEY) || "en",
    );

    /**
     * The url is the source of truth.
     *
     * If the stored firm code or email is present, but the url does not have a firm code or email,
     * redirect to the current url with the search params.
     *
     * The Verification component will verify the firm code and decide whether to show the firm code form or the login form.
     *   - If the firm code is invalid or missing, the Verification component will show the firm code form.
     *   - The firm code form will store the firm code and firm name in local storage on success.
     *
     * If the firm code is valid, the Verification component will show the login form.
     *   - The login form will store the email in local storage on success.
     */
    if ((storedFirmCode || storedEmail) && !firmCodeParam && !emailParam) {
      throw redirect({
        search: {
          firmcode: storedFirmCode,
          email: storedEmail,
          lng: storedLanguage === "es" ? "es" : "en",
          redirect: searchParams.get("redirect") || "",
        },
        replace: true,
      });
    }

    // Otherwise, simply proceed to the Verification component, which will verify the firm code and show the login form.
  },
});
